import { generate, type ToVariants } from '@/components/experiments/Core/generateBasicExperimentComponent';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useExperimentStore, useLanguageStore } from '@/helpers/composables/useStores';
import { useUserAgent } from '@/helpers/composables/useUserAgent';
import { appleTVPlusCountries } from '@/helpers/promotion-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, toValue, useMounted } from '@vueuse/core';
import { computed } from 'vue';

// ?preferred_variant=AMZ_ASSOCIATES_IMPACT_EXP:control
// ?preferred_variant=AMZ_ASSOCIATES_IMPACT_EXP:variant_1

// ?remove_preferred_experiment=AMZ_ASSOCIATES_IMPACT_EXP

export const AmzAssocImpExpName = 'AMZ_ASSOCIATES_IMPACT_EXP' as const;

export type AmzAssocImpExpVariants = ToVariants<typeof AmzAssocImpExpVariants>;

export const AmzAssocImpExpVariants = {
	CONTROL: 'control',
	VARIANT_1: 'variant_1',
} as const;

export default generate(AmzAssocImpExpName, AmzAssocImpExpVariants);

export function useAmzAssociatesImpactExp(title?: MaybeRefOrGetter<TitleDetail>) {
	const { country } = useLanguageStore();
	const isMounted = useMounted();
	const { activeVariantsWithControlGroup } = useExperimentStore();
	const { deviceType } = useUserAgent();

	const amzAssocImpExpVariant = computed(() => {
		if (!isMounted.value || !['FR', 'IT', 'DE', 'ES', 'GB'].includes(country.value)) return null;

		return activeVariantsWithControlGroup.value[AmzAssocImpExpName] as AmzAssocImpExpVariants;
	});

	const _title = computed(() => toValue(title) ?? null);

	const amzAssocImpHardcodedShortName = computed(() => {
		if (!amzAssocImpExpVariant.value || !_title.value) return undefined;

		if (
			_title.value.offers.some(offer => offer.package.shortName === 'atp') &&
			appleTVPlusCountries.includes(country.value)
		) {
			return undefined;
		}

		return ['GB', 'DE'].includes(country.value) ? 'amp' : 'prv';
	});

	function setAmzAssImpExpTag(url: ClickoutUrl, placement: 'fb' | 'ft' | 'ro' = 'ro') {
		if (!amzAssocImpExpVariant.value) return url;
		if (![9, 119].includes(url.providerId || 0)) return url;

		const device = deviceType.value === 'mobile' ? 'mob' : 'web';
		const variant = amzAssocImpExpVariant.value === 'control' ? 'ct' : 'v1';
		const tag = `aaiexp-${variant}-${country.value.toLowerCase()}-${device}-${placement}-21`;

		if (['GB', 'DE'].includes(country.value) && placement === 'fb' && amzAssocImpExpVariant.value === 'control')
			url.setOnOffer('tag', tag);

		return url.skipUCT2().set('uct_ct', tag);
	}

	return {
		amzAssocImpExpVariant,
		setAmzAssImpExpTag,
		amzAssocImpHardcodedShortName,
	};
}
